/**
 * Social Media API Module
 * Handles all social media management API calls
 */

import { fetchWithAuth, getAuthHeaders } from './client';

// Social Media API always uses production endpoint
// Local dev proxy doesn't support these endpoints
const SOCIAL_MEDIA_API = process.env.REACT_APP_SOCIAL_MEDIA_API || 'https://api.cloudmortgage.io';

// ============================================================================
// Brand Management
// ============================================================================

/**
 * Create a new brand
 * @param {Object} data - Brand data
 * @param {string} data.name - Brand name
 * @param {string} [data.logo_url] - Optional logo URL
 * @returns {Promise<BrandResponse>}
 */
export const createBrand = async (data) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/brands`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to create brand');
    }
    return response.json();
};

/**
 * Get list of user's brands
 * @returns {Promise<{ brands: Array, total: number, limit: number }>}
 */
export const getBrands = async () => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/brands`);
    if (!response.ok) {
        throw new Error('Failed to fetch brands');
    }
    return response.json();
};

/**
 * Get a single brand by ID
 * @param {string} id - Brand ID
 * @returns {Promise<BrandResponse>}
 */
export const getBrand = async (id) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/brands/${id}`);
    if (!response.ok) {
        throw new Error('Failed to fetch brand');
    }
    return response.json();
};

/**
 * Update a brand
 * @param {string} id - Brand ID
 * @param {Object} data - Updated brand data
 * @param {string} [data.name] - Brand name
 * @param {string} [data.logo_url] - Logo URL
 * @returns {Promise<BrandResponse>}
 */
export const updateBrand = async (id, data) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/brands/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to update brand');
    }
    return response.json();
};

/**
 * Delete a brand (must disconnect all accounts first)
 * @param {string} id - Brand ID
 * @returns {Promise<void>}
 */
export const deleteBrand = async (id) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/brands/${id}`, {
        method: 'DELETE',
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to delete brand');
    }
};

// ============================================================================
// Platform Management
// ============================================================================

/**
 * Get list of supported social media platforms
 * @returns {Promise<{ data: Array }>}
 */
export const getPlatforms = async () => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/platforms`);
    if (!response.ok) {
        throw new Error('Failed to fetch platforms');
    }
    return response.json();
};

/**
 * Get OAuth URL for a platform
 * @param {string} platform - Platform ID (youtube, facebook, instagram, linkedin)
 * @param {string} [brandId] - Optional brand ID to associate the integration with
 * @returns {Promise<{ platform: string, authUrl: string, state: string }>}
 */
export const getOAuthUrl = async (platform, brandId = null) => {
    const params = brandId ? `?brand_id=${encodeURIComponent(brandId)}` : '';
    const url = `${SOCIAL_MEDIA_API}/api/social-media/platforms/${platform}/oauth-url${params}`;

    console.log('[Social Media] Getting OAuth URL:', { platform, brandId, url });

    const response = await fetchWithAuth(url);
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[Social Media] OAuth URL request failed:', {
            status: response.status,
            statusText: response.statusText,
            error: errorData
        });
        throw new Error(errorData.error || errorData.message || `Failed to get OAuth URL for ${platform}`);
    }
    return response.json();
};

/**
 * Complete OAuth flow with authorization code
 * For Facebook, may return { requires_selection: true, pages: [...], oauth_state: string }
 *
 * NOTE: This endpoint does NOT require JWT auth - the backend extracts user identity
 * from the OAuth state token. This is necessary because after OAuth redirect,
 * the page reloads and React state (including auth) may be lost.
 *
 * @param {string} platform - Platform ID
 * @param {string} code - Authorization code from OAuth callback
 * @param {string} state - State parameter for CSRF verification
 * @returns {Promise<Object>} - IntegrationResponse or OAuthPagesResponse for Facebook
 */
export const completeOAuth = async (platform, code, state) => {
    // Use regular fetch, NOT fetchWithAuth - backend handles auth via state token
    const response = await fetch(`${SOCIAL_MEDIA_API}/api/social-media/platforms/${platform}/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, state }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || error.message || `Failed to complete OAuth for ${platform}`);
    }
    return response.json();
};

/**
 * Select a Facebook Page to connect (called after OAuth when pages are returned)
 *
 * NOTE: This endpoint does NOT require JWT auth - the backend extracts user identity
 * from the oauth_state token. This is necessary because after OAuth redirect,
 * the page reloads and React state (including auth) may be lost.
 *
 * @param {Object} data - Page selection data
 * @param {string} data.oauth_state - OAuth state from callback response
 * @param {string} data.page_id - Selected page ID
 * @param {string} data.page_access_token - Selected page's access token
 * @returns {Promise<IntegrationResponse>}
 */
export const selectFacebookPage = async (data) => {
    // Use regular fetch, NOT fetchWithAuth - backend handles auth via oauth_state
    const response = await fetch(`${SOCIAL_MEDIA_API}/api/social-media/platforms/facebook/select-page`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || error.message || 'Failed to connect Facebook page');
    }
    return response.json();
};

/**
 * Check if OAuth response requires page selection (for Facebook)
 * @param {Object} response - OAuth callback response
 * @returns {boolean}
 */
export const requiresPageSelection = (response) => {
    return response && response.requires_selection === true;
};

// ============================================================================
// Integration Management
// ============================================================================

/**
 * Get all connected integrations
 * @param {Object} [filters] - Optional filters
 * @param {string} [filters.platform] - Filter by platform
 * @param {string} [filters.brand_id] - Filter by brand ID
 * @returns {Promise<{ integrations: Array, total: number }>}
 */
export const getIntegrations = async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.platform) params.append('platform', filters.platform);
    if (filters.brand_id) params.append('brand_id', filters.brand_id);

    const queryString = params.toString();
    const url = `${SOCIAL_MEDIA_API}/api/social-media/integrations${queryString ? `?${queryString}` : ''}`;

    console.log('[Social Media] getIntegrations URL:', url);
    console.log('[Social Media] getIntegrations filters:', filters);

    const response = await fetchWithAuth(url);
    if (!response.ok) {
        const errorBody = await response.text().catch(() => 'No body');
        console.error('[Social Media] getIntegrations failed:', response.status, errorBody);
        throw new Error('Failed to fetch integrations');
    }
    return response.json();
};

/**
 * Get a single integration by ID
 * @param {string} id - Integration ID
 * @returns {Promise<{ data: Object }>}
 */
export const getIntegration = async (id) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/integrations/${id}`);
    if (!response.ok) {
        throw new Error('Failed to fetch integration');
    }
    return response.json();
};

/**
 * Delete/disconnect an integration
 * @param {string} id - Integration ID
 * @returns {Promise<{ success: boolean }>}
 */
export const deleteIntegration = async (id) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/integrations/${id}`, {
        method: 'DELETE',
    });
    if (!response.ok) {
        throw new Error('Failed to disconnect integration');
    }
    return response.json();
};

/**
 * Refresh an integration's access token
 * @param {string} id - Integration ID
 * @returns {Promise<{ data: Object }>}
 */
export const refreshIntegration = async (id) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/integrations/${id}/refresh`, {
        method: 'POST',
    });
    if (!response.ok) {
        throw new Error('Failed to refresh integration');
    }
    return response.json();
};

// ============================================================================
// Post Management
// ============================================================================

/**
 * Create a new post for a single platform
 * @param {Object} data - Post data
 * @param {string} data.integration_id - Target integration ID
 * @param {string} data.content - Post content
 * @param {string[]} [data.media_urls] - Optional media URLs
 * @param {string} [data.scheduled_at] - Optional ISO date for scheduling
 * @param {Object} [data.platform_settings] - Optional platform-specific settings
 * @returns {Promise<{ data: Object }>}
 */
export const createPost = async (data) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to create post');
    }
    return response.json();
};

/**
 * Create a cross-post for multiple platforms
 * @param {Object} data - Cross-post data
 * @param {string[]} data.integration_ids - Target integration IDs
 * @param {string} data.content - Post content
 * @param {string[]} [data.media_urls] - Optional media URLs
 * @param {string} [data.scheduled_at] - Optional ISO date for scheduling
 * @param {Object} [data.platform_settings] - Optional per-platform settings
 * @returns {Promise<{ data: Array }>}
 */
export const createCrossPost = async (data) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/posts/cross-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to create cross-post');
    }
    return response.json();
};

/**
 * Get posts with optional filters
 * @param {Object} [filters] - Optional filters
 * @param {string} [filters.status] - Filter by status (draft, scheduled, published, failed)
 * @param {string} [filters.integration_id] - Filter by integration
 * @param {string} [filters.start_date] - Filter posts after this date
 * @param {string} [filters.end_date] - Filter posts before this date
 * @param {number} [filters.limit] - Number of posts to return (default 20)
 * @param {string} [filters.cursor] - Pagination cursor
 * @returns {Promise<{ data: Array, cursor: string|null, has_more: boolean }>}
 */
export const getPosts = async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.integration_id) params.append('integration_id', filters.integration_id);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.cursor) params.append('cursor', filters.cursor);

    const queryString = params.toString();
    const url = `${SOCIAL_MEDIA_API}/api/social-media/posts${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url);
    if (!response.ok) {
        throw new Error('Failed to fetch posts');
    }
    return response.json();
};

/**
 * Get a single post by ID
 * @param {string} id - Post ID
 * @returns {Promise<{ data: Object }>}
 */
export const getPost = async (id) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/posts/${id}`);
    if (!response.ok) {
        throw new Error('Failed to fetch post');
    }
    return response.json();
};

/**
 * Update an existing post
 * @param {string} id - Post ID
 * @param {Object} data - Updated post data
 * @returns {Promise<{ data: Object }>}
 */
export const updatePost = async (id, data) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/posts/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to update post');
    }
    return response.json();
};

/**
 * Delete a post
 * @param {string} id - Post ID
 * @returns {Promise<{ success: boolean }>}
 */
export const deletePost = async (id) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/posts/${id}`, {
        method: 'DELETE',
    });
    if (!response.ok) {
        throw new Error('Failed to delete post');
    }
    return response.json();
};

/**
 * Publish a draft or scheduled post immediately
 * @param {string} id - Post ID
 * @returns {Promise<{ data: Object }>}
 */
export const publishPost = async (id) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/posts/${id}/publish`, {
        method: 'POST',
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to publish post');
    }
    return response.json();
};

// ============================================================================
// Analytics
// ============================================================================

/**
 * Get analytics for an integration
 * @param {string} integrationId - Integration ID
 * @param {string} [postId] - Optional specific post ID for post-level analytics
 * @param {Object} [options] - Optional parameters
 * @param {number} [options.days] - Number of days to fetch (default 30)
 * @returns {Promise<{ data: Object }>}
 */
export const getAnalytics = async (integrationId, postId = null, options = {}) => {
    const params = new URLSearchParams();
    if (postId) params.append('post_id', postId);
    if (options.days) params.append('days', options.days.toString());

    const queryString = params.toString();
    const url = `${SOCIAL_MEDIA_API}/api/social-media/analytics/${integrationId}${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url);
    if (!response.ok) {
        throw new Error('Failed to fetch analytics');
    }
    return response.json();
};

// ============================================================================
// Media Upload
// ============================================================================

/**
 * Upload media for social media posts
 * @param {File} file - File to upload
 * @param {Function} [onProgress] - Optional progress callback
 * @returns {Promise<{ url: string, type: string }>}
 */
export const uploadMedia = async (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${SOCIAL_MEDIA_API}/api/social-media/media/upload`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to upload media');
    }

    return response.json();
};

/**
 * Delete uploaded media
 * @param {string} mediaUrl - URL of media to delete
 * @returns {Promise<{ success: boolean }>}
 */
export const deleteMedia = async (mediaUrl) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/media`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: mediaUrl }),
    });

    if (!response.ok) {
        throw new Error('Failed to delete media');
    }

    return response.json();
};

// ============================================================================
// AI Image Generation
// ============================================================================

/**
 * Generate a social media image using AI
 * @param {Object} data - Generation parameters
 * @param {string} data.caption - Caption/content for the image
 * @param {string} data.style - Visual style (modern, professional, bold, minimalist, vibrant, luxury)
 * @param {string} data.instructions - Design instructions from user
 * @param {string} data.aspect_ratio - Aspect ratio (1:1, 16:9, 9:16)
 * @param {string} [data.brand_id] - Optional brand ID for branding
 * @returns {Promise<{ job_id: string, status: string }>}
 */
export const generateSocialImage = async (data) => {
    // Skip global auth handler to prevent logout on API errors
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/images/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        skipGlobalAuthHandler: true,
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        if (response.status === 401) {
            // Treat auth errors as service unavailable (backend not ready)
            const err = new Error('Image generation service is not available yet.');
            err.code = 'SERVICE_UNAVAILABLE';
            throw err;
        }
        if (response.status === 402) {
            const err = new Error('Insufficient credits');
            err.code = 'INSUFFICIENT_CREDITS';
            throw err;
        }
        if (response.status === 403) {
            const err = new Error('Subscription required');
            err.code = 'SUBSCRIPTION_REQUIRED';
            throw err;
        }
        if (response.status === 404) {
            const err = new Error('Image generation service is not available yet. Please check back later.');
            err.code = 'SERVICE_UNAVAILABLE';
            throw err;
        }
        throw new Error(error.message || error.error || 'Failed to generate image');
    }

    return response.json();
};

/**
 * Get status of an image generation job
 * @param {string} jobId - Job ID to check
 * @returns {Promise<{ job_id: string, status: string, progress: number, result_url?: string, error?: string }>}
 */
export const getSocialImageJob = async (jobId) => {
    // Skip global auth handler to prevent logout on API errors
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/images/jobs/${jobId}`, {
        skipGlobalAuthHandler: true,
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Job not found');
        }
        throw new Error('Failed to fetch job status');
    }

    return response.json();
};

/**
 * Get list of user's generated social media images
 * @param {Object} [params] - Query parameters
 * @param {number} [params.limit] - Number of images to return (default 20)
 * @param {string} [params.cursor] - Pagination cursor
 * @param {string} [params.brand_id] - Filter by brand
 * @returns {Promise<{ images: Array, cursor: string|null, has_more: boolean }>}
 */
export const getSocialImages = async (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.cursor) searchParams.append('cursor', params.cursor);
    if (params.brand_id) searchParams.append('brand_id', params.brand_id);

    const queryString = searchParams.toString();
    const url = `${SOCIAL_MEDIA_API}/api/social-media/images${queryString ? `?${queryString}` : ''}`;

    // Skip global auth handler to prevent logout if endpoint doesn't exist yet
    const response = await fetchWithAuth(url, { skipGlobalAuthHandler: true });

    if (!response.ok) {
        // Return empty result if endpoint not found (backend not implemented yet)
        if (response.status === 404) {
            return { images: [], cursor: null, has_more: false };
        }
        throw new Error('Failed to fetch images');
    }

    return response.json();
};

/**
 * Delete a generated social media image
 * @param {string} imageId - Image ID to delete
 * @returns {Promise<{ success: boolean }>}
 */
export const deleteSocialImage = async (imageId) => {
    // Skip global auth handler to prevent logout on API errors
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/images/${imageId}`, {
        method: 'DELETE',
        skipGlobalAuthHandler: true,
    });

    if (!response.ok) {
        throw new Error('Failed to delete image');
    }

    return response.json();
};

/**
 * Request professional designer service for image refinement
 * @param {Object} data - Service request data
 * @param {string[]} data.image_ids - IDs of images to improve
 * @param {string} data.instructions - What improvements to make
 * @param {File[]} [data.reference_files] - Optional reference images
 * @returns {Promise<{ order_id: string, checkout_url: string }>}
 */
export const requestDesignerService = async (data) => {
    const formData = new FormData();
    formData.append('image_ids', JSON.stringify(data.image_ids));
    formData.append('instructions', data.instructions);

    if (data.reference_files) {
        data.reference_files.forEach((file, index) => {
            formData.append(`reference_${index}`, file);
        });
    }

    // Skip global auth handler to prevent logout on API errors
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/images/designer-service`, {
        method: 'POST',
        body: formData,
        skipGlobalAuthHandler: true,
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to request designer service');
    }

    return response.json();
};

// ============================================================================
// AI Image Sessions (ChatGPT-like refinement workflow)
// ============================================================================

/**
 * Create a new image generation session
 * @param {Object} data - Session creation parameters
 * @param {string} data.caption - Initial caption/content
 * @param {string} data.style - Visual style
 * @param {string} data.instructions - Initial design instructions
 * @param {string} data.aspect_ratio - Aspect ratio
 * @param {string} [data.brand_id] - Optional brand ID
 * @returns {Promise<{ session_id: string, messages: Array, status: string }>}
 */
export const createImageSession = async (data) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/images/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        skipGlobalAuthHandler: true,
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        if (response.status === 402) {
            const err = new Error('Insufficient credits');
            err.code = 'INSUFFICIENT_CREDITS';
            throw err;
        }
        throw new Error(error.message || 'Failed to create session');
    }

    return response.json();
};

/**
 * List user's image generation sessions
 * @returns {Promise<{ sessions: Array }>}
 */
export const listImageSessions = async () => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/images/sessions`, {
        skipGlobalAuthHandler: true,
    });

    if (!response.ok) {
        if (response.status === 404) {
            return { sessions: [] };
        }
        throw new Error('Failed to fetch sessions');
    }

    return response.json();
};

/**
 * Get a specific session with messages
 * @param {string} sessionId - Session ID
 * @returns {Promise<{ session_id: string, messages: Array, status: string }>}
 */
export const getImageSession = async (sessionId) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/images/sessions/${sessionId}`, {
        skipGlobalAuthHandler: true,
    });

    if (!response.ok) {
        throw new Error('Failed to fetch session');
    }

    return response.json();
};

/**
 * Send a refinement message to a session
 * @param {string} sessionId - Session ID
 * @param {string} message - Refinement instructions
 * @returns {Promise<{ message_id: string, image_url: string, version: number }>}
 */
export const sendSessionRefinement = async (sessionId, message) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/images/sessions/${sessionId}/refine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
        skipGlobalAuthHandler: true,
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to refine image');
    }

    return response.json();
};

/**
 * Delete a session
 * @param {string} sessionId - Session ID
 * @returns {Promise<{ success: boolean }>}
 */
export const deleteImageSession = async (sessionId) => {
    const response = await fetchWithAuth(`${SOCIAL_MEDIA_API}/api/social-media/images/sessions/${sessionId}`, {
        method: 'DELETE',
        skipGlobalAuthHandler: true,
    });

    if (!response.ok) {
        throw new Error('Failed to delete session');
    }

    return response.json();
};
